import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  getCurrentUser,
  fetchUserAttributes,
  signOut as cognitoSignOut,
} from "aws-amplify/auth";
import {
  changePassword as changePasswordApi,
  getMe,
  login as loginApi,
  logout as logoutApi,
  register as registerApi,
  updateProfile as updateProfileApi,
  type AuthUser,
  type ChangePasswordPayload,
  type LoginPayload,
  type LoginResponse,
  type RegisterPayload,
  type RegisterResponse,
  type UpdateProfilePayload,
} from "../services/authApi";
import { isBackendUnreachable, clearLocalAuthToken, getLocalAuthToken } from "../services/apiClient";
import { AuthContext, type AuthContextValue } from "./auth-context";

/**
 * Attempt to build an AuthUser from the active Cognito session.
 * Returns null if no Cognito session exists.
 */
async function getCognitoUser(): Promise<AuthUser | null> {
  try {
    const { userId, signInDetails } = await getCurrentUser();
    const attrs = await fetchUserAttributes();
    const given = attrs.given_name ?? "";
    const family = attrs.family_name ?? "";
    const fullName =
      attrs.name ?? (given || family ? `${given} ${family}`.trim() : undefined);

    // Detect if this Cognito user is federated via Google.
    // Federated users have an "identities" attribute (JSON array) or a
    // username prefixed with "Google_".
    let isFederatedGoogle = false;
    try {
      const identities = attrs.identities;
      if (identities) {
        const parsed = JSON.parse(identities) as { providerName?: string }[];
        isFederatedGoogle = parsed.some(
          (id) => id.providerName?.toLowerCase() === "google",
        );
      }
    } catch {
      // identities might not be valid JSON — fall back to username check
    }
    if (
      !isFederatedGoogle &&
      (signInDetails?.loginId?.startsWith("Google_") ||
        userId.startsWith("Google_"))
    ) {
      isFederatedGoogle = true;
    }

    // Read migrated 2FA custom attributes (set by the Pre Sign-up Lambda)
    const tfaEnabled = attrs["custom:tfa_enabled"] === "true";
    const tfaMethod =
      (attrs["custom:tfa_method"] as AuthUser["twoFactorMethod"]) ?? undefined;

    return {
      id: attrs["custom:legacy_user_id"] ?? userId,
      email: attrs.email ?? "",
      name: fullName,
      role: (attrs["custom:role"] as "user" | "admin") ?? "user",
      isEmailVerified: attrs.email_verified === "true",
      profileImageUrl: attrs.picture ?? undefined,
      provider: isFederatedGoogle ? "google" : "cognito",
      isOAuthAccount: isFederatedGoogle,
      isTwoFactorEnabled: tfaEnabled || undefined,
      twoFactorMethod: tfaMethod,
    };
  } catch {
    return null;
  }
}

type TwoFactorRequiredLoginResponse = Exclude<LoginResponse, AuthUser>;

const isTwoFactorRequiredResponse = (
  response: LoginResponse,
): response is TwoFactorRequiredLoginResponse => {
  return "requires2FA" in response;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Increment to invalidate in-flight auth calls (prevents logout->old user reappearing)
  const authGeneration = useRef(0);
  const oauthCallbackHandled = useRef(false);

  const loadMe = useCallback(async () => {
    const generationAtStart = authGeneration.current;
    setIsLoading(true);
    setError(null);

    const url = new URL(window.location.href);
    const hasOAuthCode =
      url.searchParams.has("code") && url.searchParams.has("state");
    const hasOAuthError = url.searchParams.has("error");

    if (!oauthCallbackHandled.current && (hasOAuthCode || hasOAuthError)) {
      oauthCallbackHandled.current = true;

      if (hasOAuthCode) {
        try {
          const { fetchAuthSession } = await import("aws-amplify/auth");
          const { Hub } = await import("aws-amplify/utils");

          await new Promise<void>((resolve) => {
            let resolved = false;
            const complete = () => {
              if (resolved) return;
              resolved = true;
              resolve();
            };

            // Listen for Amplify Hub events indicating successful or failed sign-in
            const unsubscribe = Hub.listen("auth", ({ payload }) => {
              if (
                payload.event === "signInWithRedirect" ||
                payload.event === "signInWithRedirect_failure"
              ) {
                unsubscribe();
                complete();
              }
            });

            // Parallel polling just in case Hub event was missed or already fired
            const checkSession = async () => {
              if (resolved) return;
              try {
                const session = await fetchAuthSession();
                if (session.tokens?.idToken) {
                  unsubscribe();
                  complete();
                } else {
                  setTimeout(checkSession, 150);
                }
              } catch {
                setTimeout(checkSession, 150);
              }
            };
            void checkSession();

            // Timeout after 3 seconds
            setTimeout(() => {
              unsubscribe();
              complete();
            }, 3000);
          });
        } catch (callbackError) {
          console.error(
            "OAuth callback session exchange failed:",
            callbackError,
          );
        }
      }

      const cleanedUrl = new URL(window.location.href);
      cleanedUrl.searchParams.delete("code");
      cleanedUrl.searchParams.delete("state");
      cleanedUrl.searchParams.delete("error");
      cleanedUrl.searchParams.delete("error_description");
      window.history.replaceState(
        {},
        document.title,
        `${cleanedUrl.pathname}${cleanedUrl.search}${cleanedUrl.hash}`,
      );
    }

    // Safety timeout: if auth check takes > 8s (e.g. stale OAuth code,
    // network issue), stop loading so the UI doesn't get stuck on "Loading...".
    const timeout = setTimeout(() => {
      if (authGeneration.current === generationAtStart) {
        console.warn("Auth check timed out — forcing isLoading=false");
        setIsLoading(false);
      }
    }, 4000);

    // Check if there is any indicator that an active session exists before probing /auth/me.
    // Probing without a token or session hint causes a 401 network failure in the browser
    // console on every guest / landing page visit.
    const hasSessionHint = (() => {
      try {
        return (
          localStorage.getItem("rx_session_hint") === "1" ||
          Boolean(sessionStorage.getItem("local_auth_token")) ||
          document.cookie.includes("accessToken=") ||
          document.cookie.includes("refreshToken=")
        );
      } catch {
        return false;
      }
    })();

    // Ask Cognito if an active session exists
    const cognitoUser = await getCognitoUser();

    if (!cognitoUser && !hasSessionHint) {
      if (authGeneration.current === generationAtStart) {
        setUser(null);
        setIsLoading(false);
      }
      return null;
    }

    try {
      // Ask the backend FIRST to get MongoDB-only fields (jobRole, fieldOfWork)
      // The apiRequest tool will automatically attach the Cognito token for you.
      let me: AuthUser | null = null;
      try {
        // Recover the persistent cookie session when the short-lived access token expires.
        me = await getMe();
        try {
          localStorage.setItem("rx_session_hint", "1");
        } catch {
          // ignore storage error
        }
      } catch (meError) {
        // Fall back to bare Cognito data ONLY if the backend is unreachable.
        //
        // A 401 is not unreachability — it is the backend telling us this
        // session is dead. Falling back on it would leave a signed-out tab
        // rendering as signed-in, and every authenticated poller (the bell
        // feed, entitlements) would then retry against a session the server
        // keeps rejecting, forever.
        try {
          localStorage.removeItem("rx_session_hint");
        } catch {
          // ignore storage error
        }
        if (!isBackendUnreachable(meError)) throw meError;
        // Never treat an unverified Cognito session as an authenticated administrator
        if (cognitoUser?.role === "admin" && !getLocalAuthToken()) {
          me = null;
        } else {
          me = cognitoUser;
        }
      }

      if (authGeneration.current === generationAtStart) {
        setUser(me);
      }
      return me;
    } catch {
      try {
        localStorage.removeItem("rx_session_hint");
      } catch {
        // ignore storage error
      }
      if (authGeneration.current === generationAtStart) {
        setUser(null);
      }
      return null;
    } finally {
      clearTimeout(timeout);
      if (authGeneration.current === generationAtStart) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  const login = useCallback(
    async (payload: LoginPayload): Promise<LoginResponse> => {
      setError(null);
      authGeneration.current += 1;
      const response = await loginApi(payload);

      if (isTwoFactorRequiredResponse(response)) {
        return response;
      }

      try {
        localStorage.setItem("rx_session_hint", "1");
      } catch {
        // ignore storage error
      }
      setUser(response);
      return response;
    },
    [],
  );

  const register = useCallback(
    async (payload: RegisterPayload): Promise<RegisterResponse> => {
      setError(null);
      authGeneration.current += 1;
      const response = await registerApi(payload);
      if (!response.requiresVerification) {
        try {
          localStorage.setItem("rx_session_hint", "1");
        } catch {
          // ignore storage error
        }
        setUser(response.user);
      }
      return response;
    },
    [],
  );

  const logout = useCallback(async () => {
    setError(null);
    try {
      localStorage.removeItem("rx_session_hint");
    } catch {
      // ignore storage error
    }
    // Invalidate any in-flight loadMe/refresh so it can't re-set the old user after logout
    authGeneration.current += 1;
    try {
      // Sign out from both Cognito and the legacy REST API in parallel.
      // Use allSettled so a 401 from the old API never blocks Cognito sign-out.
      await Promise.allSettled([cognitoSignOut(), logoutApi()]);
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      clearLocalAuthToken();
      setUser(null);
    }
  }, []);

  const changePassword = useCallback(async (payload: ChangePasswordPayload) => {
    setError(null);
    const me = await changePasswordApi(payload);
    setUser(me);
    return me;
  }, []);

  const updateProfile = useCallback(async (payload: UpdateProfilePayload) => {
    setError(null);
    const me = await updateProfileApi(payload);
    setUser(me);
    return me;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      error,
      login,
      register,
      logout,
      refresh: loadMe,
      changePassword,
      updateProfile,
    }),
    [
      updateProfile,
      changePassword,
      error,
      isLoading,
      login,
      logout,
      register,
      user,
      loadMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
